/**
 * Zweck: Beschreibt die TypeScript-Bausteine für den Frontend-Bereich frontend/src/app/modules/shop/components/warenkorb-entry-edit-fields/shop.warenkorb-entry-edit-fields.component.ts.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {Artikel} from '../../../../models/artikel';
import {WarenkorbEntry} from '../../../../models/warenkorb-entry';
import {AppEventService} from '../../../../services/app.event.service';
import {DomainVertreterConfig} from '../../../../models/domain-vertreter-config';
import {BehaviorSubject, Subject} from 'rxjs';
import {AppWarenkorbService} from '../../../../services/app.warenkorb.service';
import {Warenkorb} from '../../../../models/warenkorb';
import {View} from '../../../../models/view';
import {AppDialogsService} from '../../../../services/app.dialogs.service';
import {takeUntil} from 'rxjs/operators';
import {AppUserService} from '../../../../services/app.user.service';
import {
  SharedNiceNumberInputComponent
} from '../../../shared/components/nice-number-input/shared.nice-number-input.component';
import {ShopViewInterface} from '../../interfaces/shop.view-interface';

/**
 * Zweck: Beschreibt class ShopWarenkorbEntryEditFieldsComponent und ihren Daten- oder Funktionsvertrag.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    // tslint:disable-next-line:component-selector
    selector: 'shop-entry-edit-fields',
    templateUrl: 'shop.warenkorb-entry-edit-fields.component.html',
    styleUrls: ['shop.warenkorb-entry-edit-fields.component.scss'],
    standalone: false
})
export class ShopWarenkorbEntryEditFieldsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() artikel: Artikel;
  @Input() view: View;
  @Input() viewcomp: ShopViewInterface;
  @Input() vertreterconfig: BehaviorSubject<DomainVertreterConfig>;
  @Input() disabled = false;
  @Input() disableDialog = false;
  @Input() entry: WarenkorbEntry | any;
  @Output() entryChange = new EventEmitter<WarenkorbEntry>();
  @Input() warenkorb: Warenkorb;
  @Input() view_only = false;
  @Input() islastavailable = false;
  @Output() mengeKeydownEnter = new EventEmitter<boolean>();

  editEntry: WarenkorbEntry | any;

  @ViewChild('mengeinput') mengeinput: ElementRef;

  @ViewChild('inputFreimenge') inputFreimenge: SharedNiceNumberInputComponent;
  @ViewChild('inputNaturalrabattmenge') inputNaturalrabattmenge: SharedNiceNumberInputComponent;
  @ViewChild('inputAktionsrabattmenge') inputAktionsrabattmenge: SharedNiceNumberInputComponent;
  @ViewChild('inputAktionsrabattprozent') inputAktionsrabattprozent: SharedNiceNumberInputComponent;
  @ViewChild('inputBasisverrechnungspreis') inputBasisverrechnungspreis: SharedNiceNumberInputComponent;
  @ViewChild('inputBasisverrechnungspreisprozent') inputBasisverrechnungspreisprozent: SharedNiceNumberInputComponent;
  @ViewChild('inputMenge') inputMenge: SharedNiceNumberInputComponent;
  @ViewChild('inputMenge', {read: ElementRef}) inputMengeElement: ElementRef<HTMLElement>;
  @ViewChild('inputFreimenge', {read: ElementRef}) inputFreimengeElement: ElementRef<HTMLElement>;
  @ViewChild('inputAktionsrabattmenge', {read: ElementRef}) inputAktionsrabattmengeElement: ElementRef<HTMLElement>;
  @ViewChild('inputNaturalrabattmenge', {read: ElementRef}) inputNaturalrabattmengeElement: ElementRef<HTMLElement>;

  has_change = false;
  _onDestroy = new Subject();
  focusElem: any;

  @ViewChildren(SharedNiceNumberInputComponent) niceNumberInputs: SharedNiceNumberInputComponent[];

  /** Erfasst die sichtbaren positiven Mengenfelder vor dem Zurücksetzen. */
  getPositiveMengenOrigins(): Array<{x: number, y: number}> {
    const fields: Array<[number, ElementRef<HTMLElement>]> = [
      [this.editEntry?.menge, this.inputMengeElement],
      [this.editEntry?.freimenge, this.inputFreimengeElement],
      [this.editEntry?.aktionsrabattmenge, this.inputAktionsrabattmengeElement],
      [this.editEntry?.naturalrabattmenge, this.inputNaturalrabattmengeElement]
    ];
    return fields.filter(([amount, element]) => Number(amount) > 0 && !!element).map(([, element]) => {
      const input = element.nativeElement.querySelector('input');
      const rect = (input || element.nativeElement).getBoundingClientRect();
      return rect.width > 0 && rect.height > 0
        ? {x: rect.left + rect.width / 2, y: rect.top + rect.height / 2} : null;
    }).filter(origin => origin !== null);
  }

/**
 * Beschreibt Methode constructor und ihren Rückgabevertrag.
 */
  constructor(private cd: ChangeDetectorRef, private esvc: AppEventService, private svc: AppWarenkorbService,
              private dialog: AppDialogsService, private usvc: AppUserService) {
  }

/**
 * Beschreibt Methode ngOnDestroy und ihren Rückgabevertrag.
 */
  ngOnDestroy(): void {
    this._onDestroy.next(true);
    this._onDestroy.complete();
  }

/**
 * Beschreibt Methode ngOnChanges und ihren Rückgabevertrag.
 */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entry) {
      this.init();
    }
    this.cd.markForCheck();
  }

/**
 * Beschreibt Methode validate und ihren Rückgabevertrag.
 */
  validate() {
    let valid = true;
    if (this.inputFreimenge && !this.inputFreimenge.valid) {
      valid = false;
    }
    if (this.inputNaturalrabattmenge && !this.inputNaturalrabattmenge.valid) {
      valid = false;
    }
    if (this.inputAktionsrabattmenge && !this.inputAktionsrabattmenge.valid) {
      valid = false;
    }
    if (this.inputAktionsrabattprozent && !this.inputAktionsrabattprozent.valid) {
      valid = false;
    }
    if (this.inputBasisverrechnungspreis && !this.inputBasisverrechnungspreis.valid) {
      valid = false;
    }
    if (this.inputBasisverrechnungspreisprozent && !this.inputBasisverrechnungspreisprozent.valid) {
      valid = false;
    }
    if (this.inputMenge && !this.inputMenge.valid) {
      valid = false;
    }
    this.editEntry.valid = valid;
  }

/**
 * Beschreibt Methode focusMenge und ihren Rückgabevertrag.
 */
  focusMenge() {
    if (this.mengeinput) {
      this.mengeinput.nativeElement.focus();
    } else {
      setTimeout(() => {
        this.focusMenge();
      }, 50);
    }
  }

/**
 * Beschreibt Methode mengeEnter und ihren Rückgabevertrag.
 */
  mengeEnter() {
    this.mengeKeydownEnter.emit(true);
  }

/**
 * Beschreibt Methode reset und ihren Rückgabevertrag.
 */
  reset() {
    this.detectChanges();
    this.niceNumberInputs.forEach(inp => {
      inp.detectChanges();
    });
  }

/**
 * Beschreibt Methode change und ihren Rückgabevertrag.
 */
  change() {
    this.validate();

    this.has_change = true;

    if (isNaN(this.editEntry.freimenge)) {
      this.editEntry.freimenge = parseInt(this.editEntry.freimenge, 10);
    }
    if (isNaN(this.editEntry.menge)) {
      this.editEntry.menge = parseInt(this.editEntry.menge, 10);
    }
    this.editEntry.dirty = true;

    if (String(this.editEntry.artikel?.Artikelstamm || '').toUpperCase() !== 'DE'
      && this.view && this.viewcomp && this.view.settings && this.view.settings.warning_above_amount) {
      if (this.editEntry.menge > this.view.settings.warning_above_amount) {
        this.viewcomp.showWarning(true);
      } else {
        this.viewcomp.showWarning(false);
      }
    }

    this._sendEntry();
  }

/**
 * Beschreibt Methode _sendEntry und ihren Rückgabevertrag.
 */
  _sendEntry() {
    this.entryChange.emit(this.editEntry);
    this.esvc.addGuiEvent('view_article_changed', this.artikel.ArtikelNummer);
  }

/**
 * Beschreibt Methode init und ihren Rückgabevertrag.
 */
  init() {
    this.editEntry = JSON.parse(JSON.stringify(this.entry));
    Object.setPrototypeOf(this.editEntry, WarenkorbEntry.prototype);
    this.editEntry.artikel = this.entry.artikel;

    let need_update = false;
    if (!(this.editEntry instanceof WarenkorbEntry)) {
      this.editEntry = new WarenkorbEntry();
      this.editEntry.freimenge = 0;
      this.editEntry.menge = 1;
      need_update = true;
    }


    this.editEntry.artikel = this.artikel;
    if (['NE', 'AV', 'NG'].includes(this.artikel.VerfuegbarkeitsstatusCode)) {
      if (this.artikel.IstDtpArtikel) {
        this.disabled = false;
      } else {
        this.disabled = true;
      }
    } else {
      this.disabled = false;
    }

    if (this.islastavailable) {
      this.disabled = false;
    }

    if (this.view_only) {
      this.disabled = true;
    }

    const origValid = this.editEntry.valid;

    this.validate();

    if (this.editEntry.valid !== origValid) {
      need_update = true;
    }

    if (need_update) {
      this._sendEntry();
    }
  }

/**
 * Beschreibt Methode ngOnInit und ihren Rückgabevertrag.
 */
  ngOnInit(): void {
    this.init();

    this.esvc.getQueue().pipe(takeUntil(this._onDestroy)).subscribe(e => {
      if (e.name == 'gui.setWkEntryArtikelMenge' && e.data.artnr == this.artikel.ArtikelNummer) {
        this.editEntry.menge = e.data.menge;
        this._sendEntry();
      }
    });

  }

  private detectChanges() {
    if (!this.cd['destroyed']) {
      this.cd.detectChanges();
    }
  }
}
